"use client";

import Image from "next/image";
import Link from "next/link";
import {
  ArrowLeftIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  MapPinIcon,
  ClockIcon,
  ShareIcon,
  HeartIcon,
  CheckBadgeIcon,
  BuildingStorefrontIcon,
} from "@heroicons/react/24/outline";
import { ChatBubbleLeftRightIcon } from "@heroicons/react/24/solid";
import { HeartIcon as HeartSolidIcon } from "@heroicons/react/24/solid";
import { useState } from "react";
import type { RemoteCompany } from "@/types";
import { resolveImageUrl } from "@/lib/api";

type CompanyDesktopViewProps = {
  company: RemoteCompany;
  formattedHours: string | null;
};

// Helper function to add UTM parameters to website URL
function addUtmToWebsite(website: string): string {
  const baseUrl = website.startsWith("http") ? website : `https://${website}`;
  const url = new URL(baseUrl);
  url.searchParams.set("utm_source", "santafeconecta.com.br");
  url.searchParams.set("utm_medium", "referral");
  url.searchParams.set("utm_campaign", "empresa_perfil");
  return url.toString();
}

export default function CompanyDesktopView({
  company,
  formattedHours,
}: CompanyDesktopViewProps) {
  const [isFavorite, setIsFavorite] = useState(false);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: company.name,
          text:
            company.description ||
            `Confira ${company.name} no Santa Fé Conecta`,
          url: window.location.href,
        });
      } catch {
        // User cancelled
      }
    }
  };

  const hasContactInfo =
    company.phone || company.whatsapp || company.email || company.website;
  const hasAddressInfo = company.address || company.city;

  return (
    <div className="hidden md:block bg-gray-50">
      {/* Hero Banner */}
      <div className="relative h-80 w-full overflow-hidden">
        <Image
          src={
            company.thumbnail
              ? resolveImageUrl(company.thumbnail)
              : "/bg-default-companies.avif"
          }
          alt={company.name}
          fill
          className="object-cover"
          priority
          unoptimized={!!company.thumbnail}
        />
        <div className="absolute inset-0 bg-linear-to-b from-black/60 via-black/40 to-gray-50" />

        {/* Top Navigation */}
        <div className="absolute top-0 left-0 right-0 z-20">
          <div className="max-w-7xl mx-auto px-6 py-6">
            <Link
              href="/empresas"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 backdrop-blur-md text-white hover:bg-white/20 transition-all font-medium"
            >
              <ArrowLeftIcon className="w-5 h-5" />
              Voltar para Empresas
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 -mt-32 relative z-10 pb-16">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Company Card */}
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
              {/* Header with Logo */}
              <div className="p-8 pb-6 flex items-start gap-6">
                {/* Logo */}
                <div className="relative shrink-0">
                  <div className="w-28 h-28 rounded-2xl bg-white shadow-lg ring-4 ring-gray-100 overflow-hidden">
                    {company.logo ? (
                      <Image
                        src={resolveImageUrl(company.logo)}
                        alt={`Logo ${company.name}`}
                        fill
                        className="object-cover rounded-2xl"
                        priority
                        unoptimized
                      />
                    ) : (
                      <div className="w-full h-full bg-linear-to-br from-mygreen to-mygreen/80 flex items-center justify-center">
                        <span className="text-4xl font-bold text-white">
                          {company.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                    )}
                  </div>
                  {company.verified && (
                    <div className="absolute -bottom-2 -right-2 bg-mygreen text-white p-2 rounded-xl shadow-lg">
                      <CheckBadgeIcon className="w-5 h-5" />
                    </div>
                  )}
                </div>

                {/* Company Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h1 className="text-3xl font-bold text-gray-900 mb-2">
                        {company.name}
                      </h1>
                      {company.verified && (
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-mygreen/10 text-mygreen rounded-full text-sm font-medium">
                          <CheckBadgeIcon className="w-4 h-4" />
                          Empresa Verificada
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setIsFavorite(!isFavorite)}
                        className="flex items-center justify-center w-12 h-12 rounded-xl bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-500 transition-all"
                      >
                        {isFavorite ? (
                          <HeartSolidIcon className="w-6 h-6 text-red-500" />
                        ) : (
                          <HeartIcon className="w-6 h-6" />
                        )}
                      </button>
                      <button
                        onClick={handleShare}
                        className="flex items-center justify-center w-12 h-12 rounded-xl bg-gray-100 text-gray-600 hover:bg-mygreen/10 hover:text-mygreen transition-all"
                      >
                        <ShareIcon className="w-6 h-6" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Description */}
              {company.description && (
                <div className="px-8 pb-6">
                  <div 
                    className="text-gray-600 leading-relaxed text-lg prose prose-lg max-w-none"
                    dangerouslySetInnerHTML={{ __html: company.description }}
                  />
                </div>
              )}

              {/* Categories */}
              {company.categories && company.categories.length > 0 && (
                <div className="px-8 pb-8">
                  <div className="flex flex-wrap gap-2">
                    {company.categories.map((category) => (
                      <Link
                        key={category.id}
                        href={`/categoria/${category.slug}`}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-gray-100 text-gray-700 rounded-xl text-sm font-medium hover:bg-mygreen hover:text-white transition-all"
                      >
                        <BuildingStorefrontIcon className="w-4 h-4" />
                        {category.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* CTA Buttons */}
              <div className="px-8 pb-8">
                <div className="flex flex-wrap gap-3">
                  {company.whatsapp && (
                    <a
                      href={`https://wa.me/55${company.whatsapp.replace(
                        /\D/g,
                        ""
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-8 py-4 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                    >
                      <ChatBubbleLeftRightIcon className="w-6 h-6" />
                      Conversar no WhatsApp
                    </a>
                  )}
                  {company.phone && (
                    <a
                      href={`tel:${company.phone.replace(/\D/g, "")}`}
                      className="inline-flex items-center gap-2 px-8 py-4 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5"
                    >
                      <PhoneIcon className="w-6 h-6" />
                      Ligar Agora
                    </a>
                  )}
                  {company.website && (
                    <a
                      href={addUtmToWebsite(company.website)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 px-8 py-4 bg-white text-gray-700 border-2 border-gray-200 rounded-xl font-semibold hover:border-mygreen hover:text-mygreen transition-all"
                    >
                      <GlobeAltIcon className="w-6 h-6" />
                      Visitar Website
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Location Card */}
            {hasAddressInfo && (
              <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
                <div className="p-8 pb-6">
                  <h2 className="text-xl font-bold text-gray-900 flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-xl bg-red-100 text-red-600 flex items-center justify-center">
                      <MapPinIcon className="w-5 h-5" />
                    </div>
                    Localização
                  </h2>

                  <div className="bg-gray-50 rounded-2xl p-5 mb-6">
                    {company.address && (
                      <p className="text-gray-900 font-medium text-lg">
                        {company.address}
                      </p>
                    )}
                    {(company.city || company.state) && (
                      <p className="text-gray-500 mt-1">
                        {[company.city, company.state]
                          .filter(Boolean)
                          .join(" - ")}
                      </p>
                    )}
                    {company.zip_code && (
                      <p className="text-gray-500">CEP: {company.zip_code}</p>
                    )}
                  </div>
                </div>

                {/* Map */}
                {company.address && company.city && (
                  <>
                    <div className="w-full h-80">
                      <iframe
                        className="w-full h-full"
                        style={{ border: 0 }}
                        loading="lazy"
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                        src={`https://www.google.com/maps/embed/v1/place?key=${
                          process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ||
                          "AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8"
                        }&q=${encodeURIComponent(
                          `${company.address}, ${company.city}, ${
                            company.state || "São Paulo"
                          }`
                        )}`}
                      />
                    </div>
                    <div className="p-6 bg-gray-50">
                      <a
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                          `${company.address}, ${company.city}, ${
                            company.state || "São Paulo"
                          }`
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 w-full bg-mygreen text-white py-4 rounded-xl font-semibold hover:bg-mygreen/90 transition-colors shadow-md"
                      >
                        <MapPinIcon className="w-5 h-5" />
                        Abrir no Google Maps
                      </a>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Right Column - Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            {hasContactInfo && (
              <div className="bg-white rounded-3xl shadow-xl overflow-hidden sticky top-6">
                <div className="bg-linear-to-br from-mygreen to-mygreen/90 p-6 text-white">
                  <h2 className="text-lg font-bold">Informações de Contato</h2>
                  <p className="text-white/80 text-sm mt-1">
                    Entre em contato conosco
                  </p>
                </div>

                <div className="divide-y divide-gray-100">
                  {company.phone && (
                    <a
                      href={`tel:${company.phone.replace(/\D/g, "")}`}
                      className="flex items-center gap-4 p-5 hover:bg-gray-50 transition-colors group"
                    >
                      <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors">
                        <PhoneIcon className="w-6 h-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                          Telefone
                        </p>
                        <p className="text-gray-900 font-semibold truncate">
                          {company.phone}
                        </p>
                      </div>
                    </a>
                  )}

                  {company.whatsapp && (
                    <a
                      href={`https://wa.me/55${company.whatsapp.replace(
                        /\D/g,
                        ""
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 p-5 hover:bg-gray-50 transition-colors group"
                    >
                      <div className="w-12 h-12 rounded-xl bg-green-100 text-green-600 flex items-center justify-center group-hover:bg-green-600 group-hover:text-white transition-colors">
                        <ChatBubbleLeftRightIcon className="w-6 h-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                          WhatsApp
                        </p>
                        <p className="text-gray-900 font-semibold truncate">
                          {company.whatsapp}
                        </p>
                      </div>
                    </a>
                  )}

                  {company.email && (
                    <a
                      href={`mailto:${company.email}`}
                      className="flex items-center gap-4 p-5 hover:bg-gray-50 transition-colors group"
                    >
                      <div className="w-12 h-12 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center group-hover:bg-orange-600 group-hover:text-white transition-colors">
                        <EnvelopeIcon className="w-6 h-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                          E-mail
                        </p>
                        <p className="text-gray-900 font-semibold truncate">
                          {company.email}
                        </p>
                      </div>
                    </a>
                  )}

                  {company.website && (
                    <a
                      href={addUtmToWebsite(company.website)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-4 p-5 hover:bg-gray-50 transition-colors group"
                    >
                      <div className="w-12 h-12 rounded-xl bg-purple-100 text-purple-600 flex items-center justify-center group-hover:bg-purple-600 group-hover:text-white transition-colors">
                        <GlobeAltIcon className="w-6 h-6" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500 uppercase tracking-wider font-medium">
                          Website
                        </p>
                        <p className="text-gray-900 font-semibold truncate">
                          {company.website.replace(/^https?:\/\//, "")}
                        </p>
                      </div>
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Opening Hours Card */}
            {formattedHours && (
              <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
                <div className="p-6">
                  <h2 className="text-lg font-bold text-gray-900 flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center">
                      <ClockIcon className="w-5 h-5" />
                    </div>
                    Horário de Funcionamento
                  </h2>
                  <div className="bg-gray-50 rounded-2xl p-5">
                    <pre className="text-gray-700 text-sm whitespace-pre-line font-sans leading-relaxed">
                      {formattedHours}
                    </pre>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
